import {
	useView,
	PLATFORM,
	computedFrom,
	customElement,
	bindable,
} from 'aurelia-framework';
import { BenefitsPaymentsCentreApi } from '~/api/benefits-payments-centre-api';
import {
	DynamicFieldMeta,
	DynamicFieldTable,
} from '~/components/dynamic-field-table/dynamic-field-table';
import {
	FormModelListFieldTable,
	MoveRecordDirection,
} from '~/components/dynamic-field-table/form-model-list-field-table';
import { SearchFieldModel } from '~/components/field/common/search-definition';
import { FormType } from '~/components/form/form';
import { FormModel } from '~/models/form-model';
import FormModelUtils from '~/utils/form-model-utils';
import { BenefitsPaymentsCentre } from '../benefits-payments-centre';
import {
	PaymentOptionalItemsDialogModel,
	PaymentOptionalItemsDialog,
} from '../payment-optional-items-dialog';
import {
	MessageValue,
	PaymentItemField,
	AbstractPaymentItemsList,
} from './abstract-payment-items-list';

@customElement('payment-details-list')
@useView(PLATFORM.moduleName('./payment-items-list.html'))
export class PaymentDetailsList extends AbstractPaymentItemsList {
	// Need to explicitly re-declare this since we're having issues
	// running the change handler when this isn't here.
	@bindable({
		changeHandler: 'formModelChanged',
	})
	protected formModel: FormModel;

	constructor(...rest) {
		super(...rest);
		this.calculateGlobalRowIndex = true;
		this.uniqueFieldMeta = PaymentItemField.PAYMENT_DETAIL_LINE;
		this.headerClassName = 'payment-details';
		this.headerText = 'benefits-payments-centre:payment-details';
		this.addRecordButtonTitle =
			'benefits-payments-centre:add-payment-details';
	}

	// Count of items in the form model that are Payment Detail items
	@computedFrom('formModel')
	private get tableRowsCount() {
		return this.formModel.formModelList.filter(
			(formModelItem, rowIndex) => {
				const suffix = DynamicFieldTable.getSuffix(rowIndex + 1);
				return this.isPaymentItemPartOfList(formModelItem, suffix);
			},
		).length;
	}

	@computedFrom('formModel')
	protected get isListEditable(): boolean {
		return BenefitsPaymentsCentre.isPaymentDetailsEditable(this.formModel);
	}

	@computedFrom('formModel')
	protected get renderAddRecord(): boolean {
		return this.isListEditable;
	}

	protected get renderToggleTotalMethod(): boolean {
		return false;
	}

	protected get renderTogglePreservedMethod(): boolean {
		return false;
	}

	protected formModelChangedListItemProcessing(
		childFormModel: FormModel,
		parentFormModel: FormModel,
		listRowIndex,
		suffix,
	) {
		super.formModelChangedListItemProcessing(
			childFormModel,
			parentFormModel,
			listRowIndex,
			suffix,
		);
		const tableRowIndex =
			listRowIndex -
			(parentFormModel.formModelList.length - this.tableRowsCount);
		// Conversation ID setup
		const conversationIdFieldModel: SearchFieldModel =
			childFormModel.fields[PaymentItemField.CONV_ID + suffix];
		if (conversationIdFieldModel) {
			PaymentDetailsList.setupConvIdSearchField(conversationIdFieldModel);
			PaymentDetailsList.setupConvIdAdditionalFilters(
				conversationIdFieldModel,
				tableRowIndex,
				this.selectedConversationIds,
				parentFormModel,
			);
		}
		// Set this rowIndex's selectedConversationIds
		const recordNumber = childFormModel.fields[PaymentItemField.MS_REC_NO];
		if (recordNumber) {
			this.selectedConversationIds[tableRowIndex] = recordNumber.value;
		}
		// Refresh ZY Lines Values
		PaymentDetailsList.setZYPaymentDetailLineValueToFormModelChild(
			childFormModel,
			tableRowIndex,
			listRowIndex,
		);
		// Add subscription to message field
		this.addMessageSubscription(
			childFormModel,
			listRowIndex,
			tableRowIndex,
			suffix,
		);
	}

	// Used in view
	private onAddRecord = (): void => {
		PaymentDetailsList.addPaymentDetailAndGetFormModel(
			this.formModel,
			this.listFieldMeta,
			this.tableRowsCount,
		).then((model) => (this.formModel = model));
	};

	// Used in view
	private onMoveRecord = (): void => {
		// Explictly trigger formModelChanged to refresh ZY values
		this.formModelChanged(this.formModel, this.formModel);
	};

	// Used in view
	private onRemoveRecord = (rowIndex: number, totalRows: number): void => {
		this.formModel = PaymentDetailsList.removePaymentDetailAndGetFormModel(
			this.formModel,
			rowIndex,
			totalRows,
			this.listFieldMeta,
		);
		this.selectedConversationIds[rowIndex] = '';
		this.updateSelectedConvIdFilterOfFormModel();
	};

	protected onOptionalItemsButtonClick = (
		rowNum: number,
		totalRows: number,
	): void => {
		const listRowIndex =
			this.formModel.formModelList.length - totalRows + rowNum;
		const formModel = this.formModel.formModelList[listRowIndex];
		const paymentOptionalItemsDialogModel: PaymentOptionalItemsDialogModel =
			{
				functionName: this.functionName,
				formType: FormType.EDIT,
				customisationConfig: this.customisationConfig,
				areaName: 'paymentOptionalItems',
				formModel,
				isReadOnly: !this.isListEditable,
			};
		this.classicPanelDialogUtils.showCustomClassicPanelDialog(
			PaymentOptionalItemsDialog,
			paymentOptionalItemsDialogModel,
		);
	};

	protected onSelectConvID = (
		tableRowIndex: number,
		record: { [columnName: string]: any },
		value: string,
		totalRows: number,
	): void => {
		const listRowIndex =
			this.formModel.formModelList.length - totalRows + tableRowIndex;
		this.updateConvIDField(listRowIndex, tableRowIndex, record);
	};

	protected onSelectPayee = (
		rowIndex: number,
		record: { [columnName: string]: any },
		value: string,
		totalRows: number,
	): void => {
		const listRowIndex =
			this.formModel.formModelList.length - totalRows + rowIndex;
		this.formModel = PaymentDetailsList.selectPayeeAndGetFormModel(
			this.formModel,
			listRowIndex,
			record,
			value,
		);
	};

	private static async addPaymentDetailAndGetFormModel(
		formModel: FormModel,
		paymentDetailDynamicFieldMeta: DynamicFieldMeta[],
		tableRowsCount,
	): Promise<FormModel> {
		// Check if we reached the maximum number of rows
		if (tableRowsCount >= PaymentDetailsList.MAX_ROWS_SIZE)
			return Promise.resolve(formModel);

		const formModelList = formModel.formModelList;
		let updatedFormModel: FormModel;
		const lastPaymentDetailsRowId = formModelList.length;
		// if there are no payment detail records, we need to fetch a backend formModel
		if (tableRowsCount <= 0) {
			return new Promise<FormModel>((resolve, reject) => {
				BenefitsPaymentsCentreApi.getEmptyPaymentDetail(formModel).then(
					(paymentDetail) => {
						// insert payment detail form model
						formModelList.push(paymentDetail);
						updatedFormModel = FormModelUtils.cloneModel(formModel);
						updatedFormModel.formModelList = formModelList;
						return resolve(updatedFormModel);
					},
				);
			});
		}
		// clone last payment form model
		const lastPaymentDetailsFormModel =
			formModelList[lastPaymentDetailsRowId - 1];
		const formModelToInsert = FormModelUtils.cloneModel(
			lastPaymentDetailsFormModel,
		);
		// increment row id on this payment form model
		FormModelListFieldTable.updateRowId(
			formModelToInsert.fields,
			PaymentItemField.PRIORITY,
			paymentDetailDynamicFieldMeta,
			lastPaymentDetailsRowId,
			MoveRecordDirection.DOWN,
		);
		// clear values from this payment form model
		this.defaultValuesFromInsertedRow(
			lastPaymentDetailsRowId,
			formModelToInsert,
			paymentDetailDynamicFieldMeta,
		);
		// insert payment details form model after last entry
		formModelList.push(formModelToInsert);

		updatedFormModel = FormModelUtils.cloneModel(formModel);
		updatedFormModel.formModelList = formModelList;
		return Promise.resolve(updatedFormModel);
	}

	private static removePaymentDetailAndGetFormModel(
		formModel: FormModel,
		rowIndex: number,
		totalRows: number,
		paymentDetailDynamicFieldMeta: DynamicFieldMeta[],
	): FormModel {
		const formModelList = formModel.formModelList;
		const rowIndexToRemove = formModelList.length - totalRows + rowIndex;
		const updatedFormModel = FormModelUtils.cloneModel(formModel);
		updatedFormModel.formModelList =
			PaymentDetailsList.removeRowAndGetFormModelList(
				rowIndexToRemove,
				formModelList.length,
				formModelList,
				paymentDetailDynamicFieldMeta,
			);
		return updatedFormModel;
	}

	private static setZYPaymentDetailLineValueToFormModelChild(
		formModel: FormModel,
		paymentDetailsTableRowIndex: number,
		listRowIndex: number,
	) {
		const suffix = DynamicFieldTable.getSuffix(listRowIndex + 1);
		formModel.fields[PaymentItemField.PAYMENT_DETAIL_LINE + suffix].value =
			(paymentDetailsTableRowIndex + 1).toString();
	}
}
